import { CaseWorkerRole, CaseWorkerStatus } from '@repo/shared/dtos/caseWorker'
import { Button } from '@repo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { MoreHorizontal, ShieldMinus, ShieldPlus, UserPlus, UserX } from 'lucide-react'
import { memo, useMemo } from 'react'

import { useAbility } from '@/ability/ability-context'
import { BeneficiaryListWrapper } from '@/components/admin/members/beneficiaries/List'
import { columns } from '@/components/admin/members/columns'
import { DataTable } from '@/components/admin/members/data-table'
import { Invite } from '@/components/admin/members/Invite'
import { useBeneficiaryListAsSheet } from '@/components/beneficiary/list/sheet/hook'
import { adminStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { CaseWorker } from '@/store/user'

type MembersProps = {
  caseWorkers: CaseWorker[]
}
export const Members = memo(function Members(props: MembersProps) {
  const { t, tc } = useT({ keyPrefix: 'admin.members' })
  return (
    <Card className="flex flex-1 flex-col overflow-hidden overflow-y-auto">
      <CardHeader>
        <CardTitle>{tc('members', { cfl: true })}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-6">
        <Invite />

        <MembersEditor caseWorkers={props.caseWorkers} />
      </CardContent>
    </Card>
  )
})

function MembersEditor({ caseWorkers }: MembersProps) {
  const { tc } = useT()
  const memoizedColumns = useMemo(() => columns((props) => <Action {...props} />), [])
  return (
    <>
      <CardTitle>{tc('member', { cfl: true, count: caseWorkers.length })}</CardTitle>

      <div className="max-h-screen overflow-hidden overflow-y-auto">
        <DataTable
          columns={memoizedColumns}
          data={caseWorkers}
          initialState={{
            sorting: [
              {
                id: 'lastName',
                desc: false,
              },
            ],
          }}
        />
      </div>
    </>
  )
}

const Action = memo(function ({ caseWorker }: { caseWorker: CaseWorker }) {
  const { t } = useT({ keyPrefix: 'admin.members.action' })
  const dispatch = useStoreDispatch()

  const isDisabled = caseWorker.status === CaseWorkerStatus.Disabled

  const ability = useAbility()
  const { setOpen } = useBeneficiaryListAsSheet()
  const handleCloseBeneficiaries = () => {
    setOpen(false)
  }
  const handleOpenBeneficiaries = () => {
    setOpen(true, {
      children: (
        <BeneficiaryListWrapper
          onSelectBeneficiary={handleCloseBeneficiaries}
          caseWorker={caseWorker}
        />
      ),
    })
  }
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{t('openMenu')}</span>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="select-none">
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">
                {caseWorker.firstName} {caseWorker.lastName}
              </span>
              <span className="truncate text-xs font-normal">{caseWorker.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleOpenBeneficiaries}>
            {t('viewBeneficiaries')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => void navigator.clipboard.writeText(caseWorker.id)}>
            {t('copyMemberId')}
          </DropdownMenuItem>

          {/* TODO: {caseWorker.status !== 'disabled' && (
            <>
              <DropdownMenuItem
                className="text-orange-500 focus:bg-orange-100 focus:text-orange-500"
                isSensitive
                onClick={() => {
                  // TODO:
                }}
              >
                <RotateCcw className="mr-2 size-4" />
                <span>{t('resetPassword')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )} */}

          {!isDisabled && (
            <>
              {caseWorker.role === CaseWorkerRole.CaseWorker ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-orange-500 focus:bg-orange-100 focus:text-orange-500"
                    onClick={() => {
                      void dispatch(
                        adminStoreActions.caseWorker.update({
                          caseWorkerId: caseWorker.id,
                          fields: { role: CaseWorkerRole.Admin },
                        }),
                      )
                    }}
                  >
                    <ShieldPlus className="size-4" />
                    {t('promoteToAdmin')}
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  {ability.can('disable', 'CaseWorker', caseWorker.id) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          void dispatch(
                            adminStoreActions.caseWorker.update({
                              caseWorkerId: caseWorker.id,
                              fields: { role: CaseWorkerRole.CaseWorker },
                            }),
                          )
                        }}
                      >
                        <ShieldMinus className="size-4" />
                        {t('demoteToCaseWorker')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                </>
              )}
            </>
          )}

          {isDisabled ? (
            <DropdownMenuItem
              onClick={() => {
                void dispatch(
                  adminStoreActions.caseWorker.disableOrEnable({
                    caseWorkerId: caseWorker.id,
                    disabled: false,
                  }),
                )
              }}
            >
              <UserPlus className="size-4" />
              {t('enable')}
            </DropdownMenuItem>
          ) : (
            <>
              {ability.can('disable', 'CaseWorker', caseWorker.id) && (
                <DropdownMenuItem
                  className="text-red-500 focus:bg-red-100 focus:text-red-500"
                  onClick={() => {
                    void dispatch(
                      adminStoreActions.caseWorker.disableOrEnable({
                        caseWorkerId: caseWorker.id,
                        disabled: true,
                      }),
                    )
                  }}
                >
                  <UserX className="size-4" />
                  {t('disable')}
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
})
